import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const { openAuth } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setError("Use at least 6 characters."); return; }
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-20" data-testid="reset-password-page">
      <Seo title="Reset your password — QuestionGod" />
      <div className="rounded-3xl border border-border bg-card p-8">
        <KeyRound className="h-6 w-6 text-gold" />
        <h1 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground">Choose a new password</h1>
        {!token ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="reset-missing-token">This link is incomplete. Request a new reset email from the sign-in screen.</p>
        ) : done ? (
          <div className="mt-4" data-testid="reset-success">
            <p className="text-sm text-muted-foreground">Your password has been changed and any other sessions were signed out.</p>
            <Button className="mt-5 w-full rounded-full" onClick={() => { navigate("/"); openAuth(); }} data-testid="reset-go-signin">Sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label htmlFor="new-pw">New password</Label><Input id="new-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} data-testid="reset-password-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="new-pw2">Repeat new password</Label><Input id="new-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" /></div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="reset-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password</Button>
          </form>
        )}
        <p className="mt-6 text-center text-xs text-muted-foreground"><Link to="/" className="hover:underline">Back to QuestionGod</Link></p>
      </div>
    </div>
  );
}
